import { useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { AlertTriangle } from 'lucide-react';
import env from '@/config/env';
import type { PaymentTokenStripeData } from '../api/paymentApi';
import {
  buildPaymentAmounts,
  buildPaymentContact,
  getPaymentMethodKind,
  getVehicleLabel,
  themeCssVars,
  type PaymentMethodKind,
  type PaymentTheme,
} from '../utils/paymentDisplay';
import { CompactAmountSummary } from './PaymentAmountSummary';
import { PaymentFooter } from './PaymentFooter';
import { PaymentHeader } from './PaymentHeader';
import { PaymentMethodCard } from './PaymentMethodCard';
import { PaymentStatusScreen } from './PaymentStatusScreen';
import { PaymentSummaryCard } from './PaymentSummaryCard';
import { ReassuranceBlock } from './ReassuranceBlock';

const SUBTITLES: Record<PaymentMethodKind, string> = {
  sepa_mandate: 'Mise en place de votre prélèvement mensuel par mandat SEPA.',
  sepa_single: 'Règlement de votre contrat par prélèvement SEPA.',
  card: 'Règlement de votre contrat par carte bancaire.',
};

interface PaymentCheckoutProps {
  data: PaymentTokenStripeData;
  theme: PaymentTheme;
  onSuccess: () => void;
}

/**
 * Page de paiement prête : en-tête aux couleurs du garant, récapitulatif à gauche,
 * paiement + réassurance à droite (empilés sur mobile, résumé compact en premier).
 */
export function PaymentCheckout({ data, theme, onSuccess }: PaymentCheckoutProps) {
  const stripePublicKey =
    data.isDemo && env.STRIPE_TEST_PUBLIC_KEY ? env.STRIPE_TEST_PUBLIC_KEY : env.STRIPE_PUBLIC_KEY;
  const isStripeConfigured = Boolean(stripePublicKey) && stripePublicKey !== 'false';

  // Instance Stripe unique : <Elements> refuse un changement de `stripe` après montage.
  const stripePromise = useMemo(
    () => (isStripeConfigured ? loadStripe(stripePublicKey, { stripeAccount: data.stripeAccountId }) : null),
    [isStripeConfigured, stripePublicKey, data.stripeAccountId],
  );

  const upsell = data.upsell ?? null;
  const branding = upsell?.branding ?? null;
  const methodKind = getPaymentMethodKind(data);
  const amounts = buildPaymentAmounts(data.intentType, data.metadata);
  const contact = buildPaymentContact(data);
  const vehicle = getVehicleLabel(data.metadata);

  if (!stripePromise) {
    return (
      <PaymentStatusScreen
        theme={theme}
        footerBranding={branding}
        icon={AlertTriangle}
        tone="error"
        title="Configuration manquante"
        description={<p className="tw-mb-0">Le service de paiement n&apos;est pas configuré.</p>}
      />
    );
  }

  const compactTitle = upsell?.summary.warrantyName || (vehicle ? `Contrat pour votre ${vehicle}` : 'Votre contrat');
  const compactSubtitle = upsell?.summary.warrantyName ? vehicle : null;

  return (
    <div className="tw-flex tw-min-h-screen tw-flex-col tw-bg-slate-50 tw-text-gray-900" style={themeCssVars(theme)}>
      <PaymentHeader theme={theme}>
        <p className="tw-mb-1 tw-text-sm tw-font-medium tw-opacity-80">Dernière étape</p>
        <h1 className="tw-mb-0 tw-text-2xl tw-font-bold tw-text-inherit sm:tw-text-3xl">Finalisez votre paiement</h1>
        <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-opacity-90 sm:tw-text-base">{SUBTITLES[methodKind]}</p>
      </PaymentHeader>

      <main className="tw-relative tw-mx-auto -tw-mt-14 tw-w-full tw-max-w-6xl tw-flex-1 tw-px-4 tw-pb-12 sm:-tw-mt-16 sm:tw-px-6">
        <CompactAmountSummary
          title={compactTitle}
          subtitle={compactSubtitle}
          amounts={amounts}
          className="tw-mb-4 lg:tw-hidden"
        />

        <div className="tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-12 lg:tw-items-start">
          <div className="tw-space-y-4 lg:tw-order-2 lg:tw-col-span-5">
            <PaymentMethodCard data={data} stripePromise={stripePromise} amounts={amounts} onSuccess={onSuccess} />
            <ReassuranceBlock
              methodKind={methodKind}
              contact={contact}
              retractionDays={upsell?.summary.retractionDays ?? null}
            />
          </div>

          <PaymentSummaryCard
            metadata={data.metadata}
            upsell={upsell}
            amounts={amounts}
            methodKind={methodKind}
            className="lg:tw-order-1 lg:tw-col-span-7"
          />
        </div>
      </main>

      <PaymentFooter branding={branding} />
    </div>
  );
}
